import { useEffect, useRef } from 'react';
import doc from './scan-paths.html?raw';

/** The document's inline picker script, which must be run rather than injected. */
const SCRIPT = /<script>([\s\S]*?)<\/script>/;

/**
 * Scan Paths — the traced camera-capture pipeline, mounted from the same
 * standalone HTML document that is published for people outside the console.
 *
 * WHY IT IS MOUNTED THIS WAY, and not the three obvious alternatives:
 *
 *   - NOT a static file under `public/`. Static Web Apps serves those with no
 *     auth at all, so `/scan-paths.html` would be readable by anyone with the
 *     URL — the route gate in App.tsx would be decoration.
 *   - NOT an <iframe>. `staticwebapp.config.json` sets `X-Frame-Options: DENY`
 *     and `frame-ancestors 'none'`, which blocks framing even same-origin.
 *   - NOT ported to JSX. That is a second copy of a document that changes
 *     whenever the mobile capture pipeline does, and the copies would drift.
 *     Raw-importing the authored file is the same choice PromptGuide makes for
 *     `guide.md`; this one just happens to be HTML.
 *
 * That leaves mounting the document in a SHADOW ROOT. It is not decoration:
 * the file is a whole page and styles `body`, `*`, `h1`–`h4`, `p`, `table`,
 * `th`/`td`, `code`, `section` and `footer` globally. In the light DOM those
 * rules would restyle the entire console. The shadow boundary contains them,
 * and the document's own CSS carries `:host` alongside every `:root` rule so
 * the same file themes correctly in both contexts.
 *
 * Two consequences worth knowing:
 *
 *   1. Markup set through `innerHTML` never executes its <script>, so the
 *      picker is appended as a real element and booted explicitly. The
 *      document exposes `window.__scanPathsBoot` for exactly this and
 *      self-boots only when its markup is in the document — see the comment
 *      above that script.
 *   2. `document.currentScript` is null inside a shadow tree, which is why the
 *      boot takes the root as an argument instead of discovering it.
 */
export function ScanPaths() {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;

    const shadow = el.shadowRoot ?? el.attachShadow({ mode: 'open' });
    const script = doc.match(SCRIPT);
    shadow.innerHTML = doc.replace(SCRIPT, '');

    if (script) {
      const node = document.createElement('script');
      node.textContent = script[1];
      shadow.appendChild(node);
      (window as { __scanPathsBoot?: (root: ShadowRoot) => void }).__scanPathsBoot?.(shadow);
    }
  }, []);

  // Mirror the console's theme onto the host so the document's own
  // `:host([data-theme="dark"])` palette matches the app around it. Read from
  // <html>, which ThemeModeProvider stamps, rather than from useThemeMode():
  // no context dependency, so this renders correctly under any provider tree.
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;

    const sync = () => {
      const mode = document.documentElement.dataset.theme;
      if (mode) el.setAttribute('data-theme', mode);
      else el.removeAttribute('data-theme');
    };
    sync();

    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, []);

  return <div data-testid="scan-paths" ref={hostRef} />;
}
